/**
 * RoleManagement - Manage roles and permissions with granular control
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Plus, Pencil, Trash2, Users, Lock, Eye,
  Loader2, CheckCircle, AlertCircle, Crown, Settings,
  Check, X, ChevronDown, ChevronRight, EyeOff, Play
} from 'lucide-react';
import api from '@/lib/api';

// Permission categories with descriptions
const PERMISSION_CATEGORIES = {
  'System': {
    icon: '⚙️',
    description: 'System-wide settings and administration',
    permissions: {
      '*': 'Full Access - Complete control over all features',
      'settings.system': 'System Settings - Manage global configuration',
      'settings.integrations': 'Integrations - Configure third-party services',
      'audit.view': 'Audit Log - View system activity history',
      'diagnostics.view': 'Diagnostics - View system health',
      'sample_data.generate': 'Sample Data - Generate test data',
      'ai.settings': 'AI Settings - Configure AI features'
    }
  },
  'Events': {
    icon: '📅',
    description: 'Event creation and management',
    permissions: {
      'events.create': 'Create Events - Add new events',
      'events.edit': 'Edit Events - Modify event details',
      'events.delete': 'Delete Events - Remove events',
      'events.view': 'View Events - See event information',
      'events.settings': 'Event Settings - Configure event options'
    }
  },
  'Delegates': {
    icon: '👥',
    description: 'Attendee and delegate management',
    permissions: {
      'delegates.create': 'Register Delegates - Add new attendees',
      'delegates.edit': 'Edit Delegates - Update attendee info',
      'delegates.delete': 'Delete Delegates - Remove attendees',
      'delegates.view': 'View Delegates - See attendee list',
      'delegates.export': 'Export Data - Download delegate CSV',
      'delegates.checkin': 'Check-in - Process arrivals',
      'delegates.badges': 'Print Badges - Generate name badges',
      'delegates.reset_password': 'Reset Passwords - Help attendees login'
    }
  },
  'Tickets': {
    icon: '🎫',
    description: 'Ticket tiers and pricing',
    permissions: {
      'tickets.manage': 'Manage Tickets - Create/edit ticket tiers',
      'tickets.view': 'View Tickets - See ticket information',
      'tickets.refund': 'Issue Refunds - Process ticket refunds',
      'tickets.promo_codes': 'Promo Codes - Create discount codes'
    }
  },
  'Content': {
    icon: '📝',
    description: 'Sessions, speakers, and event content',
    permissions: {
      'sessions.create': 'Create Sessions - Add new sessions',
      'sessions.edit': 'Edit Sessions - Modify session details',
      'sessions.delete': 'Delete Sessions - Remove sessions',
      'sessions.view': 'View Sessions - See schedule',
      'speakers.manage': 'Manage Speakers - Add/edit speakers',
      'sponsors.manage': 'Manage Sponsors - Add/edit sponsors',
      'exhibitors.manage': 'Manage Exhibitors - Add/edit exhibitors',
      'branding.manage': 'Manage Branding - Edit event design'
    }
  },
  'Communications': {
    icon: '📧',
    description: 'Email and messaging',
    permissions: {
      'communications.send_email': 'Send Emails - Broadcast emails',
      'communications.send_sms': 'Send SMS - Send text messages',
      'communications.templates': 'Templates - Edit message templates',
      'communications.view_history': 'View History - See sent messages'
    }
  },
  'Security': {
    icon: '🔒',
    description: 'Access control and scanning',
    permissions: {
      'security.gate_scan': 'Gate Scanning - Scan at entrance',
      'security.meal_scan': 'Meal Scanning - Scan for meals',
      'security.dorm_scan': 'Dorm Scanning - Housing access',
      'security.session_scan': 'Session Scanning - Track attendance',
      'security.gate_pass_create': 'Create Gate Passes - Request exits',
      'security.gate_pass_approve': 'Approve Gate Passes - Authorize exits',
      'security.logs_view': 'View Logs - See scan history',
      'security.alerts': 'Security Alerts - Receive alerts'
    }
  },
  'Operations': {
    icon: '🏨',
    description: 'Housing, meals, and venue',
    permissions: {
      'housing.view': 'View Housing - See assignments',
      'housing.assign': 'Assign Housing - Place attendees',
      'housing.manage': 'Manage Housing - Edit units',
      'meals.view': 'View Meals - See meal schedule',
      'meals.manage': 'Manage Meals - Edit meal options',
      'meals.dietary': 'Dietary - Handle special diets',
      'safety.incidents': 'Report Incidents - Log safety issues',
      'safety.manage': 'Manage Safety - Configure protocols',
      'venue.maps': 'Venue Maps - Edit POIs and maps'
    }
  },
  'Engagement': {
    icon: '🎯',
    description: 'Surveys, certificates, and gamification',
    permissions: {
      'surveys.create': 'Create Surveys - Add new surveys',
      'surveys.view_results': 'View Results - See responses',
      'certificates.issue': 'Issue Certificates - Generate certs',
      'certificates.view': 'View Certificates - See issued certs',
      'prizes.manage': 'Manage Prizes - Edit prize pool',
      'prizes.draw': 'Draw Prizes - Pick winners',
      'networking.manage': 'Networking - Manage connections',
      'tasks.manage': 'Manage Tasks - Create tasks',
      'tasks.complete': 'Complete Tasks - Mark done'
    }
  },
  'Finance': {
    icon: '💰',
    description: 'Payments and financial reporting',
    permissions: {
      'payments.view': 'View Payments - See transactions',
      'payments.process': 'Process Payments - Handle payments',
      'payments.refund': 'Issue Refunds - Process refunds',
      'payments.reports': 'Financial Reports - Generate reports',
      'economy.manage': 'Virtual Economy - Manage in-app currency'
    }
  },
  'Analytics': {
    icon: '📊',
    description: 'Reports and data analysis',
    permissions: {
      'analytics.view': 'View Analytics - See dashboards',
      'analytics.export': 'Export Data - Download reports',
      'analytics.department': 'Department Stats - Team metrics',
      'analytics.realtime': 'Realtime - Live statistics'
    }
  },
  'Team': {
    icon: '👤',
    description: 'Team member management',
    permissions: {
      'team.view': 'View Team - See members',
      'team.invite': 'Invite Members - Add team members',
      'team.manage': 'Manage Team - Edit roles',
      'team.remove': 'Remove Members - Delete accounts'
    }
  }
};

// Flatten permissions for backward compatibility
const FLAT_PERMISSION_CATEGORIES = Object.fromEntries(
  Object.entries(PERMISSION_CATEGORIES).map(([cat, data]) => [
    cat, 
    Object.keys(data.permissions)
  ])
);

const RoleManagement = () => {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewRole, setPreviewRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({ name: '', description: '', color: '#6b7280', permissions: [] });
  const [expandedCategories, setExpandedCategories] = useState({});

  // Fetch roles
  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get('/admin/roles').then(r => r.data),
  });

  // Fetch permissions
  const { data: permissions = [] } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.get('/admin/permissions').then(r => r.data),
  });

  // Create role mutation
  const createRoleMutation = useMutation({
    mutationFn: (data) => api.post('/admin/roles', data),
    onSuccess: () => {
      toast.success('Role created successfully');
      queryClient.invalidateQueries(['roles']);
      setIsCreateOpen(false);
      setNewRole({ name: '', description: '', color: '#6b7280', permissions: [] });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create role')
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/admin/roles/${id}`, data),
    onSuccess: () => {
      toast.success('Role updated successfully');
      queryClient.invalidateQueries(['roles']);
      setIsEditOpen(false);
      setEditingRole(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update role')
  });

  // Delete role mutation
  const deleteRoleMutation = useMutation({
    mutationFn: (id) => api.delete(`/admin/roles/${id}`),
    onSuccess: () => {
      toast.success('Role deleted successfully');
      queryClient.invalidateQueries(['roles']);
      setSelectedRole(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete role')
  });

  const togglePermission = (permission, roleData, setRoleData) => {
    const current = roleData.permissions || [];
    const updated = current.includes(permission)
      ? current.filter(p => p !== permission)
      : [...current, permission];
    setRoleData(prev => ({ ...prev, permissions: updated }));
  };

  const toggleCategory = (category, roleData, setRoleData) => {
    const perms = Object.keys(PERMISSION_CATEGORIES[category]?.permissions || {});
    const current = roleData.permissions || [];
    const allSelected = perms.every(p => current.includes(p));
    
    if (allSelected) {
      // Remove all from category
      setRoleData(prev => ({ ...prev, permissions: current.filter(p => !perms.includes(p)) }));
    } else {
      // Add all from category
      const newPerms = [...new Set([...current, ...perms])];
      setRoleData(prev => ({ ...prev, permissions: newPerms }));
    }
  };

  const toggleCategoryExpand = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const openEditDialog = (role) => {
    setEditingRole({ ...role });
    setIsEditOpen(true);
  };

  const openPreviewDialog = (role) => {
    setPreviewRole(role);
    setIsPreviewOpen(true);
  };

  // Define menu items with their required permissions
  const MENU_ITEMS_WITH_PERMISSIONS = [
    { label: 'Dashboard', icon: '📊', permissions: [], always: true },
    { label: 'Events', icon: '📅', permissions: ['events.view', 'events.create', 'events.edit'] },
    { label: 'Delegates', icon: '👥', permissions: ['delegates.view', 'delegates.create', 'delegates.edit'] },
    { label: 'Check-in', icon: '✓', permissions: ['delegates.checkin', 'security.gate_scan'] },
    { label: 'Sessions', icon: '🎯', permissions: ['sessions.view', 'sessions.create'] },
    { label: 'Speakers', icon: '🎤', permissions: ['speakers.manage', 'content.*'] },
    { label: 'Sponsors', icon: '🤝', permissions: ['sponsors.manage'] },
    { label: 'Exhibitors', icon: '🏢', permissions: ['exhibitors.manage'] },
    { label: 'Housing', icon: '🏨', permissions: ['housing.view', 'housing.assign', 'housing.manage'] },
    { label: 'Meals', icon: '🍽️', permissions: ['meals.view', 'meals.manage', 'security.meal_scan'] },
    { label: 'Security', icon: '🔐', permissions: ['security.gate_scan', 'security.meal_scan', 'security.gate_pass_approve'] },
    { label: 'Communications', icon: '📧', permissions: ['communications.send_email', 'communications.send_sms'] },
    { label: 'Finance', icon: '💰', permissions: ['payments.view', 'payments.process', 'payments.reports'] },
    { label: 'Analytics', icon: '📈', permissions: ['analytics.view', 'analytics.export'] },
    { label: 'Surveys', icon: '📋', permissions: ['surveys.create', 'surveys.view_results'] },
    { label: 'Certificates', icon: '🏆', permissions: ['certificates.issue', 'certificates.view'] },
    { label: 'Team', icon: '👤', permissions: ['team.view', 'team.invite', 'team.manage'] },
    { label: 'Roles', icon: '🛡️', permissions: ['*', 'settings.system'], superAdminOnly: true },
    { label: 'Settings', icon: '⚙️', permissions: ['settings.system', 'settings.integrations', '*'], superAdminOnly: true },
    { label: 'AI Tools', icon: '🤖', permissions: ['ai.settings', '*'] },
  ];

  // Check if role has permission
  const roleHasPermission = (rolePerms, requiredPerms) => {
    if (!rolePerms) return false;
    if (rolePerms.includes('*')) return true;
    if (requiredPerms.length === 0) return true;
    return requiredPerms.some(p => {
      if (rolePerms.includes(p)) return true;
      // Check category wildcard
      if (p.includes('.')) {
        const category = p.split('.')[0];
        if (rolePerms.includes(`${category}.*`)) return true;
      }
      return false;
    });
  };

  // Preview component showing menu access
  const RolePreview = ({ role }) => {
    const perms = role?.permissions || [];
    const isSuperAdmin = perms.includes('*') || role?.name?.toLowerCase().includes('super');

    return (
      <div className="space-y-4">
        {/* Warning banner for preview mode */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <Eye className="h-5 w-5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-800">Preview Mode</p>
            <p className="text-xs text-amber-600">This shows what users with the "{role?.name}" role can see and access</p>
          </div>
        </div>

        {/* Menu Access Preview */}
        <div>
          <h4 className="font-medium mb-3">Menu Access</h4>
          <div className="grid grid-cols-2 gap-2">
            {MENU_ITEMS_WITH_PERMISSIONS.map(item => {
              const hasAccess = item.always || 
                (item.superAdminOnly ? isSuperAdmin : roleHasPermission(perms, item.permissions));
              
              return (
                <div 
                  key={item.label}
                  className={`flex items-center gap-2 p-2 rounded-lg border ${
                    hasAccess 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-slate-50 border-slate-200 opacity-50'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span className={`text-sm ${hasAccess ? 'text-green-800' : 'text-slate-400 line-through'}`}>
                    {item.label}
                  </span>
                  {hasAccess ? (
                    <Check className="h-4 w-4 text-green-600 ml-auto" />
                  ) : (
                    <X className="h-4 w-4 text-slate-400 ml-auto" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Permissions Preview */}
        <div>
          <h4 className="font-medium mb-3">Available Actions</h4>
          <div className="space-y-2">
            {Object.entries(PERMISSION_CATEGORIES).map(([category, data]) => {
              const categoryPerms = Object.entries(data.permissions);
              const availablePerms = categoryPerms.filter(([perm]) => roleHasPermission(perms, [perm]));
              
              if (availablePerms.length === 0) return null;
              
              return (
                <div key={category} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span>{data.icon}</span>
                    <span className="font-medium text-sm">{category}</span>
                    <Badge variant="outline" className="text-xs ml-auto">
                      {availablePerms.length}/{categoryPerms.length}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {availablePerms.map(([perm, desc]) => (
                      <Badge key={perm} variant="secondary" className="text-xs">
                        {desc.split(' - ')[0]}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Restricted Areas */}
        <div>
          <h4 className="font-medium mb-3 text-red-600">Restricted Areas</h4>
          <div className="space-y-1">
            {MENU_ITEMS_WITH_PERMISSIONS
              .filter(item => !item.always && !(item.superAdminOnly ? isSuperAdmin : roleHasPermission(perms, item.permissions)))
              .map(item => (
                <div key={item.label} className="flex items-center gap-2 text-sm text-slate-500">
                  <EyeOff className="h-3 w-3" />
                  <span>{item.label}</span>
                </div>
              ))
            }
            {MENU_ITEMS_WITH_PERMISSIONS.every(item => 
              item.always || (item.superAdminOnly ? isSuperAdmin : roleHasPermission(perms, item.permissions))
            ) && (
              <p className="text-sm text-green-600">
                <CheckCircle className="h-4 w-4 inline mr-1" />
                This role has full menu access
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Permission editor component
  const PermissionEditor = ({ roleData, setRoleData, disabled = false }) => (
    <div className="space-y-2 max-h-[400px] overflow-y-auto border rounded-lg p-3">
      {Object.entries(PERMISSION_CATEGORIES).map(([category, data]) => {
        const perms = Object.keys(data.permissions);
        const selectedCount = perms.filter(p => roleData.permissions?.includes(p)).length;
        const allSelected = selectedCount === perms.length;
        const someSelected = selectedCount > 0 && !allSelected;
        const isExpanded = expandedCategories[category];
        
        return (
          <div key={category} className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleCategoryExpand(category)}
              disabled={disabled}
              className={`w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 ${disabled ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{data.icon}</span>
                <div>
                  <span className="font-medium">{category}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    ({selectedCount}/{perms.length})
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!disabled && (
                  <Switch
                    checked={allSelected}
                    onCheckedChange={(checked) => {
                      // Prevent event during render
                      setTimeout(() => toggleCategory(category, roleData, setRoleData), 0);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </button>
            
            {isExpanded && (
              <div className="border-t bg-slate-50 p-3 space-y-2">
                <p className="text-xs text-slate-500 mb-2">{data.description}</p>
                {Object.entries(data.permissions).map(([perm, desc]) => (
                  <label 
                    key={perm} 
                    className={`flex items-start gap-3 p-2 rounded hover:bg-white cursor-pointer ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <Checkbox
                      checked={roleData.permissions?.includes(perm) || false}
                      onCheckedChange={(checked) => {
                        if (!disabled) {
                          setTimeout(() => togglePermission(perm, roleData, setRoleData), 0);
                        }
                      }}
                      disabled={disabled}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{desc.split(' - ')[0]}</div>
                      <div className="text-xs text-slate-500">{desc.split(' - ')[1] || perm}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Backend returns `is_system: true` for built-in roles. Older variants used
  // `is_system_role` — accept both so we don't accidentally show Edit/Delete on
  // system roles when the API returns the canonical name.
  const isSystem = (r) => Boolean(r.is_system || r.is_system_role);
  const systemRoles = roles.filter(isSystem);
  const customRoles = roles.filter((r) => !isSystem(r));

  return (
    <div className="space-y-6" data-testid="role-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-indigo-600" />
            Roles & Permissions
          </h2>
          <p className="text-slate-500">Define what each team member can do</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-role-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Custom Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Create Custom Role</DialogTitle>
              <DialogDescription>
                Define a new role with specific permissions
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role Name</Label>
                  <Input
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    placeholder="e.g., Marketing Manager"
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={newRole.color}
                      onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={newRole.color}
                      onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  placeholder="Describe what this role is for"
                />
              </div>
              <div>
                <Label className="mb-3 block">
                  Permissions 
                  <Badge variant="outline" className="ml-2">
                    {newRole.permissions.length} selected
                  </Badge>
                </Label>
                <PermissionEditor roleData={newRole} setRoleData={setNewRole} />
              </div>
            </div>
            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => createRoleMutation.mutate(newRole)}
                disabled={!newRole.name || createRoleMutation.isPending}
              >
                {createRoleMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: editingRole?.color }}
                />
                Edit Role: {editingRole?.name}
              </DialogTitle>
              <DialogDescription>
                Modify role permissions and details
              </DialogDescription>
            </DialogHeader>
            {editingRole && (
              <>
                <div className="space-y-4 py-4 flex-1 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Role Name</Label>
                      <Input
                        value={editingRole.name}
                        onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                        placeholder="Role name"
                      />
                    </div>
                    <div>
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={editingRole.color}
                          onChange={(e) => setEditingRole({ ...editingRole, color: e.target.value })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={editingRole.color}
                          onChange={(e) => setEditingRole({ ...editingRole, color: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editingRole.description}
                      onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                      placeholder="Role description"
                    />
                  </div>
                  <div>
                    <Label className="mb-3 block">
                      Permissions
                      <Badge variant="outline" className="ml-2">
                        {editingRole.permissions?.length || 0} selected
                      </Badge>
                    </Label>
                    <PermissionEditor roleData={editingRole} setRoleData={setEditingRole} />
                  </div>
                </div>
                <DialogFooter className="border-t pt-4">
                  <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={() => updateRoleMutation.mutate({ id: editingRole.id, data: editingRole })}
                    disabled={!editingRole.name || updateRoleMutation.isPending}
                  >
                    {updateRoleMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Preview Role Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-indigo-600" />
                Preview Role: {previewRole?.name}
              </DialogTitle>
              <DialogDescription>
                See what users with this role can access in the system
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="flex-1 pr-4">
              {previewRole && <RolePreview role={previewRole} />}
            </ScrollArea>
            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close Preview</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Role List */}
          <div className="lg:col-span-1 space-y-4">
            {/* System Roles */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Crown className="h-4 w-4 text-amber-500" />
                  System Roles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {systemRoles.map(role => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedRole?.id === role.id 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: role.color }}
                        />
                        <span className="font-medium">{role.name}</span>
                      </div>
                      <Lock className="h-3 w-3 text-slate-400" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{role.description}</p>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Custom Roles */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings className="h-4 w-4 text-slate-500" />
                  Custom Roles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {customRoles.length > 0 ? customRoles.map(role => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedRole?.id === role.id 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: role.color }}
                      />
                      <span className="font-medium">{role.name}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{role.description}</p>
                  </button>
                )) : (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No custom roles yet
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Role Details */}
          <div className="lg:col-span-2">
            {selectedRole ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: selectedRole.color }}
                      />
                      <div>
                        <CardTitle>{selectedRole.name}</CardTitle>
                        <CardDescription>{selectedRole.description}</CardDescription>
                      </div>
                    </div>
                    {!isSystem(selectedRole) && (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openPreviewDialog(selectedRole)}
                          data-testid="preview-role-btn"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditDialog(selectedRole)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteRoleMutation.mutate(selectedRole.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {isSystem(selectedRole) && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openPreviewDialog(selectedRole)}
                        data-testid="preview-system-role-btn"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview Access
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant={isSystem(selectedRole) ? 'secondary' : 'outline'}>
                        {isSystem(selectedRole) ? (
                          <><Lock className="h-3 w-3 mr-1" />System Role</>
                        ) : 'Custom Role'}
                      </Badge>
                      {selectedRole.permissions?.includes('*') && (
                        <Badge className="bg-amber-100 text-amber-800">
                          <Crown className="h-3 w-3 mr-1" />
                          Full Access
                        </Badge>
                      )}
                      <Badge variant="outline">
                        {selectedRole.permissions?.includes('*') ? '∞' : selectedRole.permissions?.length || 0} permissions
                      </Badge>
                    </div>

                    <div>
                      <h4 className="font-medium mb-3">Permissions by Category</h4>
                      {selectedRole.permissions?.includes('*') ? (
                        <div className="p-4 bg-amber-50 rounded-lg text-amber-800 text-sm">
                          <Crown className="h-4 w-4 inline mr-2" />
                          This role has full access to all permissions
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {Object.entries(PERMISSION_CATEGORIES).map(([category, data]) => {
                            const perms = Object.keys(data.permissions);
                            const hasPerms = perms.filter(p => selectedRole.permissions?.includes(p));
                            if (hasPerms.length === 0) return null;
                            
                            return (
                              <div key={category} className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-lg">{data.icon}</span>
                                  <h5 className="font-medium text-slate-700">{category}</h5>
                                  <Badge variant="outline" className="text-xs">
                                    {hasPerms.length}/{perms.length}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {hasPerms.map(perm => (
                                    <Badge key={perm} variant="secondary" className="text-xs">
                                      <Check className="h-3 w-3 mr-1 text-green-600" />
                                      {data.permissions[perm]?.split(' - ')[0] || perm}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Show categories without access */}
                          <div className="mt-4 pt-4 border-t">
                            <h5 className="text-sm font-medium text-slate-500 mb-2">No Access</h5>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(PERMISSION_CATEGORIES).map(([category, data]) => {
                                const perms = Object.keys(data.permissions);
                                const hasPerms = perms.filter(p => selectedRole.permissions?.includes(p));
                                if (hasPerms.length > 0) return null;
                                
                                return (
                                  <Badge key={category} variant="outline" className="text-xs text-slate-400">
                                    <X className="h-3 w-3 mr-1" />
                                    {category}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Select a role to view details</p>
                  <p className="text-xs text-slate-400 mt-1">Click on any role from the list</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
